import { useContext, useMemo, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React from 'react';
import { getRoutesForRestaurant, pageRoutes } from '../../config/routeConfig';
import SideHeaderLink from './SideHeaderLink';
import SideHeaderButton from './SideHeaderButton';
import LogoutIcon from '@mui/icons-material/Logout';
import { t } from 'i18next';
import AuthContext, { AuthState } from '../../contexts/AuthContext';
import { searchMatch } from '../../utils/textUtils';
import { Popper, Grow, Paper, ClickAwayListener, MenuList, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { RightType } from '../../models/Auth';


export default function SideHeader() {
    const [isExpanded, setIsExpanded] = useState(false);
    const { logout, tableManagementOn, userInfo, myRestaurants, selectRestaurant, authState } = useContext(AuthContext);
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const displayedRestaurants = useMemo(() => {
        const nonSelected = myRestaurants.filter(restaurant => restaurant.fb_id !== userInfo?.selected_restaurant?.fb_id);
        return nonSelected.filter(restaurant => searchMatch(search, restaurant.name));
    }, [myRestaurants, search, userInfo?.selected_restaurant]);


    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }

    function handleOptionChange(fbId: string) {
        selectRestaurant(fbId);
        setOpen(false);
    }

    const displayedPages = useMemo(() => {
        if (userInfo == null || userInfo.selected_restaurant == null) return [];
        return getRoutesForRestaurant(
            tableManagementOn, 
            userInfo?.selected_restaurant.group_reservations, 
            userInfo?.selected_restaurant.waitlist,
            userInfo?.right_type as RightType,
        );
    }, [tableManagementOn, userInfo?.selected_restaurant]);

    function expandContainerClassNames() {
        if (isExpanded) {
            return "flex gap-1";
        }
        return "text-center ";
    }

    function buttonClassName() {
        if (myRestaurants.length > 1) {
            return "hover:bg-primary-dark";
        }
        return "";
    }

    if (displayedPages.length === 0 && authState === AuthState.LOGGED_IN) {
        return null;
    }

    return (
        <div
            className={`bg-primary-dark text-white h-screen flex flex-col items-start
          ${isExpanded ? 'w-64' : 'w-[70px]'} transition-all duration-300 ease-in-out`}>
            {/* Toggle Button */}
            <div className="bg-primary pt-[18px] pb-[10px] rounded-b-[15px] px-2 w-full">
                <div className={`${expandContainerClassNames()}`}>
                    <div className="grow">
                        {isExpanded && (
                            <button
                                ref={anchorRef}
                                onClick={handleToggle}
                                className={`${buttonClassName()} text-white font-bold h-[44px] px-2  transition-all rounded-[8px]`}
                                disabled={myRestaurants.length <= 1}
                            >
                                <div className="flex">
                                    <div>
                                        {userInfo?.selected_restaurant?.name}
                                    </div>
                                </div>
                            </button>
                        )}
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="bg-transparent rounded-[10px] hover:bg-primary-dark p-1 transition-all"
                    >
                        {isExpanded && (
                            <ArrowBackIcon
                                className="text-white"
                                style={{ fontSize: "36px" }}
                            />
                        )}
                        {!isExpanded && (
                            <MenuIcon
                                className="text-white"
                                style={{ fontSize: "36px" }}
                            />
                        )}
                    </button>
                </div>
            </div>
            {/* Menu Items */}
            <nav className="flex flex-col gap-[10px] px-2 mt-2 w-full">
                {displayedPages.map(route => {
                    return <SideHeaderLink
                        key={route.title}
                        icon={route.icon}
                        label={route.headerTitle}
                        path={route.path}
                        isExpanded={isExpanded}
                        title={route.title}
                    />
                })}
                <SideHeaderButton
                    icon={<LogoutIcon />}
                    label={t('common.logout')}
                    isExpanded={isExpanded}
                    onClick={() => logout()}
                />
            </nav>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="top-start"
                transition
                disablePortal
                style={{ zIndex: 1000, textAlign: "center", width: `300px` }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom-start' ? 'left top' : 'left bottom',
                            marginLeft: "10px",
                            marginRight: "10px",
                        }}
                    >
                        <Paper
                            style={{ maxHeight: "360px", overflowY: "auto", }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <div
                                    className=""
                                >
                                    {myRestaurants.length > 3 && (
                                        <div className="px-2 pt-2">
                                            <div className="flex w-full border-solid border-b-[1px] border-primary-dark">
                                                <input
                                                    type="text"
                                                    placeholder={t("common.search")}
                                                    className="grow w-full bg-transparent font-bold text-primary-dark placeholder:text-mutedIcon placeholder:font-bold"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                >
                                                </input>
                                                <div className="text-utility-mutedIcon">
                                                    <SearchIcon
                                                        style={{ fontSize: "24px" }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {displayedRestaurants.length === 0 && (
                                        <div className="text-utility-mutedIcon text-[16px] pt-2">
                                            {t("common.no_restaurant")}
                                        </div>
                                    )}
                                    <MenuList
                                        autoFocusItem={false}
                                        onKeyDown={handleListKeyDown}
                                    >
                                        {displayedRestaurants.map(option => {
                                            return <MenuItem
                                                key={option.fb_id}
                                                onClick={() => handleOptionChange(option.fb_id)}

                                            >
                                                {option.name}
                                            </MenuItem>
                                        })}
                                    </MenuList>
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
}